import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../../utils/api';
import { useToast, ToastHost } from '../../../components/portal/Toast';
import Pagination, { PAGE_SIZE } from '../../../components/portal/Pagination';
import {
  RiCalendarCheckLine, RiSearchLine, RiCheckLine, RiCloseLine,
  RiDeleteBinLine, RiTimeLine, RiFilterLine, RiRefreshLine,
  RiCalendarLine, RiErrorWarningLine,
} from 'react-icons/ri';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const LEAVE_TYPE_LABEL = {
  full_day:    'Full Day',
  first_half:  'First Half',
  second_half: 'Second Half',
};

const STATUS_CONFIG = {
  pending:  { badge: 'badge text-[#f59e0b] bg-[rgba(245,158,11,0.08)] border-[rgba(245,158,11,0.2)]',  dot: '#f59e0b', label: 'Pending'  },
  approved: { badge: 'badge text-[#10b981] bg-[rgba(16,185,129,0.08)] border-[rgba(16,185,129,0.2)]', dot: '#10b981', label: 'Approved' },
  rejected: { badge: 'badge text-red-500 bg-red-50 border-red-100',                                    dot: '#ef4444', label: 'Rejected' },
};

const LEAVE_TYPE_BADGE = {
  full_day:    'badge text-[#0984e3] bg-[rgba(9,132,227,0.08)] border-[rgba(9,132,227,0.2)]',
  first_half:  'badge text-[#8b5cf6] bg-[rgba(139,92,246,0.08)] border-[rgba(139,92,246,0.2)]',
  second_half: 'badge text-[#d500f9] bg-[rgba(213,0,249,0.06)] border-[rgba(213,0,249,0.2)]',
};

export default function AdminLeaves() {
  const today = new Date();

  // ── server-side filters ───────────────────────────────────────────────────
  // Month/year picker — defaults to the current month.
  const [filterMonth, setFilterMonth] = useState(today.getMonth() + 1);
  const [filterYear,  setFilterYear]  = useState(today.getFullYear());
  const [filterDate,  setFilterDate]  = useState('');   // specific date (overrides month)
  const [filterUserId, setFilterUserId] = useState('');   // employee UUID

  // ── client-side filters ───────────────────────────────────────────────────
  const [filterStatus, setFilterStatus] = useState('all');
  const [search,       setSearch]       = useState('');

  // ── data ──────────────────────────────────────────────────────────────────
  const [leaves,    setLeaves]    = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  // Load employee list once for the dropdown
  useEffect(() => {
    api.get('/users/employees')
      .then((res) => {
        const d = res.data;
        const list = d?.users || d?.employees || d?.data || (Array.isArray(d) ? d : []);
        setEmployees(list);
      })
      .catch(() => {});
  }, []);

  const [actionId,   setActionId]   = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { toast, showToast, clearToast } = useToast();

  // ── rejection reason modal ────────────────────────────────────────────────
  const [rejectTarget, setRejectTarget] = useState(null); // { id, name }
  const [rejectReason, setRejectReason] = useState('');


  // ── pagination state ──────────────────────────────────────────────────────
  const [page,  setPage]  = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search by 300ms before firing the API call
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when any server-side filter changes
  useEffect(() => { setPage(1); }, [filterMonth, filterYear, filterDate, filterStatus, debouncedSearch, filterUserId]);

  // ── fetch: date overrides month; when neither is set, backend returns ALL ──
  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = { page, limit: PAGE_SIZE };
    if (filterDate) {
      params.date = filterDate;
    } else if (filterMonth && filterYear) {
      params.month = filterMonth;
      params.year  = filterYear;
    }
    // else: no date/month/year sent → backend returns all leaves (no pagination)
    if (filterStatus !== 'all') params.status = filterStatus;
    if (debouncedSearch)        params.search = debouncedSearch;
    if (filterUserId)           params.user_id = filterUserId;

    api.get('/leaves', { params })
      .then((res) => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.leaves || d?.data || [];
        setLeaves(list);
        setTotal(d?.meta?.total ?? list.length);
      })
      .catch(() => setError('Failed to load leaves.'))
      .finally(() => setLoading(false));
  }, [filterMonth, filterYear, filterDate, filterStatus, page, debouncedSearch, filterUserId]);

  useEffect(() => { load(); }, [load]);

  // clear date filter when month/year changes

  // ── actions ───────────────────────────────────────────────────────────────
  const handleStatus = async (id, status, reason = '') => {
    setActionId(id);
    try {
      const payload = reason ? { status, rejection_reason: reason } : { status };
      await api.patch(`/leaves/${id}/status`, payload);
      setLeaves((prev) => prev.map((l) =>
        (l._id || l.id) === id ? { ...l, status, rejection_reason: reason || l.rejection_reason } : l
      ));
      window.dispatchEvent(new CustomEvent('leaves:changed'));
      showToast(`Leave ${status}.`, 'success');
    } catch (err) {
      showToast(err.response?.data?.message || `Failed to ${status} leave.`, 'error');
    } finally {
      setActionId(null);
    }
  };

  const openRejectModal  = (id, name) => { setRejectTarget({ id, name }); setRejectReason(''); };
  const closeRejectModal = () => { setRejectTarget(null); setRejectReason(''); };
  const submitRejection  = async () => {
    await handleStatus(rejectTarget.id, 'rejected', rejectReason);
    closeRejectModal();
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/leaves/${id}`);
      setLeaves((prev) => prev.filter((l) => (l._id || l.id) !== id));
      window.dispatchEvent(new CustomEvent('leaves:changed'));
      showToast('Leave deleted.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete leave.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  // Search is now server-side, so the loaded leaves are already filtered.
  const filtered = leaves;

  // ── stats ─────────────────────────────────────────────────────────────────
  // `total` (server) is exact for the active filter set;
  // status counts are computed from current page (use the status pills for exact counts).
  const stats = useMemo(() => ({
    total,
    pending:      leaves.filter((l) => l.status === 'pending').length,
    approved:     leaves.filter((l) => l.status === 'approved').length,
    rejected:     leaves.filter((l) => l.status === 'rejected').length,
    approvedDays: leaves.filter((l) => l.status === 'approved')
                        .reduce((s, l) => s + (Number(l.total_days) || 0), 0).toFixed(1),
  }), [leaves, total]);

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  const activeLabel = filterDate
    ? new Date(filterDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : (filterMonth && filterYear)
      ? `${MONTH_NAMES[filterMonth - 1]} ${filterYear}`
      : 'All time';

  const hasClientFilters = search || filterStatus !== 'all';

  return (
    <div className="p-7 space-y-6 bg-[#F8FAFC]">
      <ToastHost toast={toast} clearToast={clearToast} />

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="label-meta mb-1">Management</p>
          <h1 className="heading-md text-[#1F2937]">Leave Requests</h1>
          <p className="text-[#6B7280] text-sm mt-1">
            {loading ? 'Loading…' : `${total} leave${total !== 1 ? 's' : ''} — ${activeLabel}`}
          </p>
        </div>
        <button
          onClick={load}
          className="btn flex items-center gap-2 text-[#6B7280]"
          style={{ padding: '0.6rem 1.1rem', fontSize: '13px' }}
        >
          <RiRefreshLine size={14} /> Refresh
        </button>
      </div>

      {/* ── Stats ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: 'Total',         value: stats.total,        gradient: 'from-[#0984e3] to-[#00b0ff]', shadow: 'rgba(9,132,227,0.25)',  icon: RiCalendarCheckLine },
          { label: 'Pending',       value: stats.pending,      gradient: 'from-[#f59e0b] to-[#fbbf24]', shadow: 'rgba(245,158,11,0.25)', icon: RiTimeLine },
          { label: 'Approved',      value: stats.approved,     gradient: 'from-[#10b981] to-[#34d399]', shadow: 'rgba(16,185,129,0.25)', icon: RiCheckLine },
          { label: 'Rejected',      value: stats.rejected,     gradient: 'from-[#ef4444] to-[#f87171]', shadow: 'rgba(239,68,68,0.25)',  icon: RiCloseLine },
          { label: 'Approved Days', value: stats.approvedDays, gradient: 'from-[#8b5cf6] to-[#d500f9]', shadow: 'rgba(139,92,246,0.25)', icon: RiCalendarCheckLine },
        ].map(({ label, value, gradient, shadow, icon: Icon }) => (
          <div key={label} className="bg-white border border-[#E5E7EB] rounded-2xl p-5 shadow-[0_4px_20px_rgba(15,23,42,0.06)]">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-3`} style={{ boxShadow: `0 6px 16px ${shadow}` }}>
              <Icon size={18} className="text-white" />
            </div>
            <p className="text-[#1F2937] text-2xl font-bold">{loading ? '—' : value}</p>
            <p className="text-[#9CA3AF] text-xs mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Filter card ─────────────────────────────────────────────────── */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] p-5">
        <div className="flex items-center gap-2 mb-4">
          <RiFilterLine size={14} className="text-[#0984e3]" />
          <span className="text-xs font-semibold text-[#374151] uppercase tracking-wide">Filters</span>
          {hasClientFilters && (
            <button
              onClick={() => { setSearch(''); setFilterStatus('all'); }}
              className="ml-auto text-xs text-[#0984e3] font-semibold hover:underline cursor-pointer flex items-center gap-1"
            >
              <RiCloseLine size={12} /> Clear
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">

          {/* Month / Year — clearable native picker. Empty = show ALL leaves. */}
          <div>
            <label className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">
              Month
              <span className="ml-1 normal-case font-normal tracking-normal text-[#C4C9D4]">(clear = all)</span>
            </label>
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1 min-w-0">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" size={14} />
                <input
                  type="month"
                  value={filterMonth && filterYear ? `${filterYear}-${String(filterMonth).padStart(2, '0')}` : ''}
                  min="2005-01"
                  onChange={(e) => {
                    const [y, m] = (e.target.value || '').split('-');
                    if (y && m) {
                      setFilterYear(Number(y));
                      setFilterMonth(Number(m));
                      setFilterDate('');
                    } else {
                      // user cleared the input via the browser's native clear control
                      setFilterMonth(null);
                      setFilterYear(null);
                    }
                  }}
                  className="input-field pl-8 text-sm w-full cursor-pointer"
                />
              </div>
              {(filterMonth || filterYear) && (
                <button
                  onClick={() => { setFilterMonth(null); setFilterYear(null); }}
                  className="flex-shrink-0 w-7 h-[38px] rounded-lg flex items-center justify-center text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 border border-[#E5E7EB] transition-all cursor-pointer"
                  title="Clear — show all leaves"
                >
                  <RiCloseLine size={15} />
                </button>
              )}
            </div>
          </div>

          {/* Specific date (overrides month + year) */}
          <div>
            <label className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">
              Specific date
            </label>
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1 min-w-0">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" size={14} />
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="input-field pl-8 text-sm w-full cursor-pointer"
                />
              </div>
              {filterDate && (
                <button
                  onClick={() => setFilterDate('')}
                  className="flex-shrink-0 w-7 h-[38px] rounded-lg flex items-center justify-center text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 border border-[#E5E7EB] transition-all cursor-pointer"
                  title="Clear date — show whole month"
                >
                  <RiCloseLine size={15} />
                </button>
              )}
            </div>
          </div>

          {/* Employee */}
          <div>
            <label className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">Employee</label>
            <select
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              className="input-field cursor-pointer text-sm"
            >
              <option value="">All employees</option>
              {employees.map((u) => {
                const id = u.id || u._id;
                return (
                  <option key={id} value={id}>
                    {u.name || u.username || u.email_id}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">Search</label>
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" size={14} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or email…"
                className="input-field pl-8 text-sm"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#374151] cursor-pointer">
                  <RiCloseLine size={14} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Status pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mr-1">Status</span>
          {['all', 'pending', 'approved', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer capitalize ${
                filterStatus === s
                  ? 'text-white border-transparent'
                  : 'text-[#6B7280] border-[#E5E7EB] hover:border-[#0984e3] hover:text-[#0984e3]'
              }`}
              style={filterStatus === s ? { background: 'linear-gradient(135deg,#0984e3,#d500f9)', boxShadow: '0 4px 12px rgba(9,132,227,0.25)' } : {}}
            >
              {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label}
              {s !== 'all' && !loading && (
                <span className="ml-1.5 opacity-70">
                  {s === 'pending' ? stats.pending : s === 'approved' ? stats.approved : stats.rejected}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* active filter pills */}
        {(filterDate || search || filterStatus !== 'all' || filterUserId) && (
          <div className="flex items-center gap-2 mt-3 flex-wrap pt-3 border-t border-[#F3F4F6]">
            <span className="text-xs text-[#9CA3AF]">Active:</span>
            {filterDate && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-[#0984e3] bg-[rgba(9,132,227,0.08)] border border-[rgba(9,132,227,0.2)]">
                <RiCalendarLine size={11} />
                {fmtDate(filterDate + 'T00:00:00')}
                <button onClick={() => setFilterDate('')} className="ml-0.5 cursor-pointer"><RiCloseLine size={11} /></button>
              </span>
            )}
            {filterUserId && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-[#10b981] bg-[rgba(16,185,129,0.08)] border border-[rgba(16,185,129,0.2)]">
                {(() => {
                  const u = employees.find((e) => (e.id || e._id) === filterUserId);
                  return u?.name || u?.username || u?.email_id || 'Employee';
                })()}
                <button onClick={() => setFilterUserId('')} className="ml-0.5 cursor-pointer"><RiCloseLine size={11} /></button>
              </span>
            )}
            {filterStatus !== 'all' && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-[#7c3aed] bg-[rgba(124,58,237,0.08)] border border-[rgba(124,58,237,0.2)]">
                {STATUS_CONFIG[filterStatus]?.label}
                <button onClick={() => setFilterStatus('all')} className="ml-0.5 cursor-pointer"><RiCloseLine size={11} /></button>
              </span>
            )}
            {search && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-[#374151] bg-[#F3F4F6] border border-[#E5E7EB]">
                <RiSearchLine size={11} />"{search}"
                <button onClick={() => setSearch('')} className="ml-0.5 cursor-pointer"><RiCloseLine size={11} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center justify-between">
          {error}
          <button onClick={load} className="text-xs font-semibold hover:underline cursor-pointer ml-4">Retry</button>
        </div>
      )}

      {/* ── Leave list ──────────────────────────────────────────────────── */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
          <h2 className="text-[#1F2937] font-bold">Leave Requests</h2>
          <span className="badge">{total}</span>
        </div>

        {loading ? (
          <div className="divide-y divide-[#F3F4F6]">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="px-6 py-4 flex items-center gap-4">
                <div className="w-9 h-9 bg-[#F3F4F6] rounded-xl flex-shrink-0 animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-[#F3F4F6] rounded w-1/4 animate-pulse" />
                  <div className="h-3 bg-[#F3F4F6] rounded w-2/5 animate-pulse" />
                </div>
                <div className="h-7 w-20 bg-[#F3F4F6] rounded-lg animate-pulse" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-2xl bg-[#F3F4F6] flex items-center justify-center mx-auto mb-3">
              <RiCalendarCheckLine size={28} className="text-[#D1D5DB]" />
            </div>
            <p className="text-[#374151] text-sm font-semibold">
              {hasClientFilters ? 'No leaves match your filters.' : `No leave requests for ${activeLabel}.`}
            </p>
            {hasClientFilters && (
              <button
                onClick={() => { setSearch(''); setFilterStatus('all'); }}
                className="mt-3 text-[#0984e3] text-sm font-medium hover:underline cursor-pointer"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-[#F3F4F6]">
            {filtered.map((l) => {
              const id          = l._id || l.id;
              const name        = l.user?.name || l.employee?.name || l.userName || 'Employee';
              const email       = l.user?.username || l.employee?.username || l.user?.email_id || l.employee?.email_id || l.userEmail || '';
              const initials    = name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
              const isPending   = l.status === 'pending';
              const isActioning = actionId === id;
              const isDeleting  = deletingId === id;

              return (
                <div key={id} className="px-6 py-4 hover:bg-[#FAFBFC] transition-colors">
                  <div className="flex items-start justify-between gap-4 flex-wrap">

                    {/* Employee + leave info */}
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg,#0984e3 0%,#d500f9 100%)' }}
                      >
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <p className="text-[#1F2937] text-sm font-semibold">{name}</p>
                          <span className={LEAVE_TYPE_BADGE[l.leave_type] || 'badge'}>
                            {LEAVE_TYPE_LABEL[l.leave_type] || l.leave_type}
                          </span>
                          <span className={STATUS_CONFIG[l.status]?.badge || 'badge capitalize'}>{l.status}</span>
                          {l.total_days != null && (
                            <span className="badge text-[#6B7280] bg-[#F3F4F6] border-[#E5E7EB]">
                              {l.total_days} day{l.total_days !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {email && <p className="text-[#9CA3AF] text-xs mb-1">{email}</p>}
                        <p className="text-[#6B7280] text-xs flex items-center gap-1">
                          <RiCalendarLine size={11} />
                          {fmtDate(l.start_date)}
                          {l.end_date && l.end_date !== l.start_date ? ` → ${fmtDate(l.end_date)}` : ''}
                        </p>
                        {l.reason && (
                          <p className="text-[#6B7280] text-xs mt-1 line-clamp-2">{l.reason}</p>
                        )}
                        {l.rejection_reason && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <RiErrorWarningLine size={11} />
                            Reason: {l.rejection_reason}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isPending && (
                        <>
                          <button
                            onClick={() => handleStatus(id, 'approved')}
                            disabled={isActioning}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#10b981] bg-[rgba(16,185,129,0.08)] border border-[rgba(16,185,129,0.2)] hover:bg-[rgba(16,185,129,0.15)] transition-colors cursor-pointer disabled:opacity-50"
                          >
                            {isActioning
                              ? <span className="w-3.5 h-3.5 border border-[#10b981] border-t-transparent rounded-full animate-spin" />
                              : <RiCheckLine size={13} />}
                            Approve
                          </button>
                          <button
                            onClick={() => openRejectModal(id, name)}
                            disabled={isActioning}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-500 bg-red-50 border border-red-100 hover:bg-red-100 transition-colors cursor-pointer disabled:opacity-50"
                          >
                            <RiCloseLine size={13} />
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(id)}
                        disabled={isDeleting}
                        className="p-1.5 text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer disabled:opacity-50"
                        aria-label="Delete"
                      >
                        {isDeleting
                          ? <span className="w-3.5 h-3.5 border border-red-400 border-t-transparent rounded-full animate-spin block" />
                          : <RiDeleteBinLine size={15} />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pagination page={page} total={total} onChange={setPage} />
      </div>

      {/* ── Reject reason modal ─────────────────────────────────────────────── */}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(15,23,42,0.18)] w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <RiErrorWarningLine size={20} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-[#1F2937] font-bold text-base">Reject Leave</h3>
                <p className="text-[#6B7280] text-xs mt-0.5">
                  Rejecting leave for <span className="font-semibold text-[#374151]">{rejectTarget.name}</span>
                </p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">
                Reason <span className="normal-case font-normal text-[#9CA3AF]">(optional)</span>
              </label>
              <textarea
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Let the employee know why their leave was rejected…"
                className="input-field resize-none w-full text-sm"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={closeRejectModal}
                className="flex-1 px-4 py-2.5 rounded-xl border border-[#E5E7EB] text-[#374151] text-sm font-semibold hover:bg-[#F8FAFC] transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={submitRejection}
                disabled={actionId === rejectTarget.id}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {actionId === rejectTarget.id
                  ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  : <RiCloseLine size={15} />}
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
